/**
 * v5.9 Addition 1 — New Deal Approvals page.
 * List + detail view. Accept/Reject actions.
 * Auto-Populating Bot integration lands in the next slice — on Accept for
 * now, the user is taken to the New Deal wizard seeded with candidate info.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { useTerm } from "@/lib/terminology.jsx";
import { formatDay } from "@/lib/format";
import { Inbox, Search, Paperclip, AlertTriangle, ExternalLink,
         CheckCircle2, XCircle, ArrowLeft, Mail, ScrollText } from "lucide-react";

export default function NewDealApprovals() {
  const nav = useNavigate();
  const { user } = useAuth();
  const term = useTerm();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showLog, setShowLog] = useState(false);
  const [logRows, setLogRows] = useState(null);
  const [params] = useSearchParams();
  const openId = params.get("item");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/approvals", { params: { status } });
      setItems(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load approvals");
    }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (showLog && !logRows) {
      api.get("/ingestion/log").then(({ data }) => setLogRows(data));
    }
  }, [showLog, logRows]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((i) => {
      if (typeFilter !== "all" && (i.content_category || "likely_new_deal") !== typeFilter) return false;
      if (!s) return true;
      return (i.candidate_company_name || "").toLowerCase().includes(s)
        || (i.subject || "").toLowerCase().includes(s)
        || (i.from_address || "").toLowerCase().includes(s);
    });
  }, [items, search, typeFilter]);

  if (openId) {
    return <ApprovalDetail itemId={openId} onBack={() => nav("/approvals")} onDone={load} />;
  }

  return (
    <div className="p-8">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="cw-caption uppercase tracking-widest">Inbound</div>
          <h1 className="cw-h1 mt-1 inline-flex items-center gap-2">
            <Inbox size={24} /> New {term("Deal")} Approvals
          </h1>
          <p className="text-sm text-[var(--cw-muted)] mt-1">
            Emails forwarded to your intake mailbox land here. Review and Accept to create a {term("deal").toLowerCase()}, or Reject.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="cw-btn-ghost text-xs inline-flex items-center gap-1"
            onClick={() => setShowLog(true)} data-testid="view-log-btn">
            <ScrollText size={12} /> View log
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-md border overflow-hidden" style={{ borderColor: "var(--cw-border)" }}>
          {[
            { k: "pending", l: "Pending" },
            { k: "accepted", l: "In progress" },
            { k: "rejected", l: "Rejected" },
          ].map((b) => (
            <button key={b.k}
              onClick={() => setStatus(b.k)}
              className={`text-xs px-3 py-1.5 ${status === b.k ? "font-semibold" : "text-[var(--cw-muted)]"}`}
              style={status === b.k ? { background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" } : {}}
              data-testid={`approvals-scope-${b.k}`}
            >
              {b.l}
            </button>
          ))}
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          className="h-9 px-3 rounded-md border text-xs bg-white"
          style={{ borderColor: "var(--cw-border)" }}
          data-testid="approvals-type-filter">
          <option value="all">All types</option>
          <option value="likely_new_deal">New {term("Deal")}</option>
          <option value="likely_followup">Follow-up</option>
          <option value="meeting_transcript">Meeting Transcript</option>
        </select>
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute top-1/2 -translate-y-1/2 left-3"
            style={{ color: "var(--cw-muted)" }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company, subject, sender…"
            className="w-full h-9 pl-9 pr-3 rounded-md border text-sm"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="approvals-search"
          />
        </div>
      </div>

      <div className="mt-4 cw-card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--cw-muted)]">
            No {status === "pending" ? "pending" : status} items.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead style={{ background: "rgba(40,42,116,0.05)" }}>
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">Company</th>
                <th className="px-4 py-3 font-semibold">From</th>
                <th className="px-4 py-3 font-semibold">Subject</th>
                <th className="px-4 py-3 font-semibold">Received</th>
                <th className="px-4 py-3 font-semibold text-right">Attachments</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.item_id}
                    className="border-t cursor-pointer hover:bg-[rgba(40,42,116,0.04)]"
                    style={{ borderColor: "var(--cw-border)" }}
                    onClick={() => nav(`/approvals?item=${i.item_id}`)}
                    data-testid={`approval-row-${i.item_id}`}>
                  <td className="px-4 py-3 font-medium">
                    {i.candidate_company_name || <span className="text-[var(--cw-muted)] italic">(no company detected)</span>}
                    <CategoryBadge category={i.content_category} term={term} />
                    {i.possible_duplicate_of_id && (
                      <span className="ml-2 cw-badge"
                        style={{ background: "rgba(243,191,92,0.20)", color: "#8A6510" }}
                        title="Possible duplicate">
                        <AlertTriangle size={10} /> Possible duplicate
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[var(--cw-muted)] truncate max-w-[200px]">
                    {i.from_address || "—"}
                  </td>
                  <td className="px-4 py-3 truncate max-w-[300px]">{i.subject}</td>
                  <td className="px-4 py-3 text-[var(--cw-muted)]">{formatDay(i.received_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {i.attachment_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs text-[var(--cw-muted)]">
                        <Paperclip size={12} /> {i.attachment_count}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showLog && (
        <IngestionLogModal rows={logRows} onClose={() => setShowLog(false)} />
      )}
    </div>
  );
}

/* -------------------- Detail view -------------------- */
function ApprovalDetail({ itemId, onBack, onDone }) {
  const nav = useNavigate();
  const term = useTerm();
  const [data, setData] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pipelineChoice, setPipelineChoice] = useState("");
  const [pipelines, setPipelines] = useState([]);

  useEffect(() => {
    api.get(`/approvals/${itemId}`).then(({ data }) => setData(data));
    api.get("/pipelines").then(({ data }) => {
      setPipelines(data);
      if (data.length === 1) setPipelineChoice(data[0].pipeline_id);
    });
  }, [itemId]);

  if (!data) return <div className="p-10 text-[var(--cw-muted)]">Loading…</div>;
  const item = data.item;
  const isPending = item.status === "pending";

  const doAccept = async () => {
    if (pipelines.length > 1 && !pipelineChoice) {
      toast.error("Choose a pipeline first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/approvals/${itemId}/accept`,
        { pipeline_id: pipelineChoice || null });
      toast.success("Extraction started — mapping fields");
      nav(`/agent-runs/${data.run_id}/map`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept");
      setBusy(false);
    }
  };

  const doAttach = async (dealId) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/approvals/${itemId}/attach`,
        { deal_id: dealId });
      toast.success(`Attached to ${term("deal").toLowerCase()}`);
      nav(`/deals/${res.deal_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to attach");
      setBusy(false);
    }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/approvals/${itemId}/reject`, { rejection_reason: rejectReason.trim() });
      toast.success("Rejected");
      onBack();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reject");
    } finally {
      setBusy(false);
    }
  };

  const downloadAttachment = async (a) => {
    try {
      const { data } = await api.get(`/approvals/${itemId}/attachment/${a.attachment_id}`);
      if (!data.data_base64) {
        toast.error("Attachment bytes not available");
        return;
      }
      const link = document.createElement("a");
      link.href = `data:${a.file_type ? "application/octet-stream" : "application/octet-stream"};base64,${data.data_base64}`;
      link.download = data.filename;
      link.click();
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="p-8 max-w-[1200px] mx-auto">
      <button onClick={onBack}
        className="text-sm inline-flex items-center gap-1 text-[var(--cw-muted)] hover:text-[var(--cw-primary)]"
        data-testid="back-to-approvals">
        <ArrowLeft size={14} /> Back to Approvals
      </button>

      <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[400px]">
          <div className="cw-caption uppercase tracking-widest">Incoming email</div>
          <h1 className="cw-h1 mt-1 break-words">{item.subject || "(no subject)"}</h1>
          <div className="mt-2 flex items-center gap-4 text-sm text-[var(--cw-muted)] flex-wrap">
            <span><Mail size={12} className="inline mr-1" />{item.from_address || "—"}</span>
            <span>Received {formatDay(item.received_at)}</span>
            {item.candidate_company_name && (
              <span className="cw-badge" style={{ background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" }}>
                Detected: <strong>{item.candidate_company_name}</strong>
                {item.candidate_company_domain && ` (${item.candidate_company_domain})`}
              </span>
            )}
          </div>
          {item.possible_duplicate_of_id && (
            <div className="mt-3 rounded-md p-3"
              style={{ background: "rgba(243,191,92,0.15)", color: "#7A5900" }}>
              <div className="text-xs font-semibold uppercase tracking-widest">
                <AlertTriangle size={12} className="inline mr-1" /> Possible duplicate
              </div>
              <div className="text-sm mt-1">
                This might duplicate an existing {item.possible_duplicate_of_type === "deal" ? term("deal").toLowerCase() : "pending approval item"}.
                {item.possible_duplicate_of_type === "deal" && (
                  <a href={`/deals/${item.possible_duplicate_of_id}`}
                    className="ml-2 underline">Open matching {term("deal").toLowerCase()}</a>
                )}
              </div>
            </div>
          )}
        </div>

        {isPending && (
          <div className="flex flex-wrap gap-2 items-center">
            {pipelines.length > 1 && (
              <select value={pipelineChoice} onChange={(e) => setPipelineChoice(e.target.value)}
                className="h-9 px-3 rounded-md border text-sm bg-white"
                style={{ borderColor: "var(--cw-border)" }}
                data-testid="accept-pipeline-select">
                <option value="">Choose {term("pipeline").toLowerCase()}…</option>
                {pipelines.map((p) => (
                  <option key={p.pipeline_id} value={p.pipeline_id}>{p.pipeline_name}</option>
                ))}
              </select>
            )}
            <button onClick={doAccept} disabled={busy}
              className={
                (item.content_category === "likely_new_deal"
                  ? "cw-btn-secondary"
                  : "cw-btn-ghost")
                + " text-sm inline-flex items-center gap-1"}
              data-testid="approval-accept-btn">
              <CheckCircle2 size={14} /> Accept as New {term("Deal")}
            </button>
            <button onClick={() => setAttaching(true)} disabled={busy}
              className={
                (item.content_category !== "likely_new_deal"
                  ? "cw-btn-secondary"
                  : "cw-btn-ghost")
                + " text-sm inline-flex items-center gap-1"}
              data-testid="approval-attach-btn">
              <Paperclip size={14} /> Attach to Existing {term("Deal")}
            </button>
            <button onClick={() => setRejecting(true)} disabled={busy}
              className="text-sm inline-flex items-center gap-1 rounded-md px-3 py-1.5 border transition"
              style={{ borderColor: "var(--cw-error)", color: "var(--cw-error)" }}
              data-testid="approval-reject-btn">
              <XCircle size={14} /> Reject
            </button>
          </div>
        )}
        {item.status === "rejected" && (
          <span className="cw-badge" style={{ background: "rgba(214,69,69,0.15)", color: "#8A2A2A" }}>
            <XCircle size={12} /> Rejected
          </span>
        )}
        {item.status === "accepted" && (
          <span className="cw-badge" style={{ background: "rgba(44,154,82,0.15)", color: "#1F6E3A" }}>
            <CheckCircle2 size={12} /> Accepted
          </span>
        )}
      </div>

      {/* Attachments */}
      <div className="mt-6 cw-card p-5">
        <h3 className="cw-h3 mb-3">Attachments & Links</h3>
        {data.attachments.length === 0 ? (
          <div className="text-xs text-[var(--cw-muted)] italic">No attachments or document links detected.</div>
        ) : (
          <div className="space-y-2">
            {data.attachments.filter((a) => a.kind === "file").map((a) => (
              <div key={a.attachment_id}
                className="flex items-center justify-between px-3 py-2 rounded-md border"
                style={{ borderColor: "var(--cw-border)" }}
                data-testid={`attachment-${a.attachment_id}`}>
                <div className="flex items-center gap-2">
                  <Paperclip size={14} className="text-[var(--cw-muted)]" />
                  <div>
                    <div className="text-sm font-medium">{a.original_filename}</div>
                    <div className="text-xs text-[var(--cw-muted)]">
                      {a.file_type?.toUpperCase()} · {formatBytes(a.size_bytes)}
                    </div>
                  </div>
                </div>
                <button onClick={() => downloadAttachment(a)}
                  className="cw-btn-ghost text-xs"
                  data-testid={`download-${a.attachment_id}`}>
                  Download
                </button>
              </div>
            ))}
            {data.attachments.filter((a) => a.kind === "link").map((a) => (
              <div key={a.attachment_id}
                className="flex items-center justify-between px-3 py-2 rounded-md border"
                style={{ borderColor: "var(--cw-border)" }}>
                <div className="flex items-center gap-2">
                  <ExternalLink size={14} className="text-[var(--cw-muted)]" />
                  <div className="min-w-0">
                    <div className="text-sm truncate max-w-[500px]">
                      <a href={a.link_url} target="_blank" rel="noreferrer" className="underline">
                        {a.link_url}
                      </a>
                    </div>
                    <div className="text-xs text-[var(--cw-muted)]">
                      {a.link_status === "unreachable" && "Couldn't be opened automatically — open it and attach the file manually if needed."}
                      {a.link_status === "auto_downloaded" && "Auto-downloaded"}
                      {a.link_status === "manually_attached" && "Manually attached"}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Email body */}
      <div className="mt-4 cw-card p-5">
        <h3 className="cw-h3 mb-3">Email body</h3>
        {item.body_html ? (
          <iframe title="email-body"
            srcDoc={sanitizeHtml(item.body_html)}
            className="w-full min-h-[300px] rounded-md border"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="email-body-html" />
        ) : (
          <pre className="text-sm whitespace-pre-wrap font-sans"
            data-testid="email-body-text">
            {item.body_text || "(empty body)"}
          </pre>
        )}
      </div>

      {rejecting && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          data-testid="reject-modal">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="px-6 py-4 border-b" style={{ borderColor: "var(--cw-border)" }}>
              <h2 className="cw-h2 text-[var(--cw-error)]">Reject email</h2>
            </div>
            <div className="p-6 space-y-3">
              <div className="text-xs text-[var(--cw-muted)]">
                Reason (required) — recorded permanently in the Flow Log.
              </div>
              <textarea value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full min-h-[80px] px-3 py-2 rounded-md border text-sm"
                style={{ borderColor: "var(--cw-border)" }}
                data-testid="reject-reason-input" />
            </div>
            <div className="px-6 py-3 border-t flex justify-end gap-2"
              style={{ borderColor: "var(--cw-border)" }}>
              <button onClick={() => setRejecting(false)} className="cw-btn-ghost text-sm">Cancel</button>
              <button onClick={doReject} disabled={busy || !rejectReason.trim()}
                className="text-sm inline-flex items-center gap-1 rounded-md px-3 py-1.5 border transition disabled:opacity-40"
                style={{ borderColor: "var(--cw-error)", color: "var(--cw-error)" }}
                data-testid="reject-confirm-btn">
                {busy ? "Rejecting…" : "Reject permanently"}
              </button>
            </div>
          </div>
        </div>
      )}

      {attaching && (
        <AttachToDealModal
          candidates={item.candidate_matches || []}
          onClose={() => setAttaching(false)}
          onPick={doAttach}
          busy={busy}
        />
      )}
    </div>
  );
}

/* -------------------- Ingestion Log Modal -------------------- */
function IngestionLogModal({ rows, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="ingestion-log-modal">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        <div className="px-6 py-4 border-b flex justify-between items-center"
          style={{ borderColor: "var(--cw-border)" }}>
          <h2 className="cw-h2 inline-flex items-center gap-2"><ScrollText size={20} /> Ingestion Log</h2>
          <button onClick={onClose} className="text-[var(--cw-muted)] text-lg">×</button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          {!rows && <div className="text-[var(--cw-muted)] text-sm">Loading…</div>}
          {rows && (
            <>
              <div>
                <h3 className="cw-h3 mb-2">Rejected</h3>
                {rows.rejected.length === 0 ? (
                  <div className="text-xs text-[var(--cw-muted)]">Nothing rejected.</div>
                ) : (
                  <LogTable rows={rows.rejected.map((r) => ({
                    company: r.candidate_company_name || "—",
                    from: r.from_address,
                    subject: r.subject,
                    when: r.rejected_at || r.received_at,
                    note: r.rejection_reason,
                  }))} />
                )}
              </div>
              <div>
                <h3 className="cw-h3 mb-2">Suppressed duplicates</h3>
                {rows.suppressed_duplicates.length === 0 ? (
                  <div className="text-xs text-[var(--cw-muted)]">No duplicates suppressed.</div>
                ) : (
                  <LogTable rows={rows.suppressed_duplicates.map((r) => ({
                    company: r.candidate_company_name || "—",
                    from: r.from_address,
                    subject: r.subject,
                    when: r.processed_at,
                    note: `Duplicate of ${r.duplicate_of_type}`,
                  }))} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function LogTable({ rows }) {
  return (
    <table className="w-full text-xs">
      <thead style={{ background: "rgba(40,42,116,0.05)" }}>
        <tr className="text-left">
          <th className="px-3 py-2">Company</th>
          <th className="px-3 py-2">From</th>
          <th className="px-3 py-2">Subject</th>
          <th className="px-3 py-2">When</th>
          <th className="px-3 py-2">Note</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t" style={{ borderColor: "var(--cw-border)" }}>
            <td className="px-3 py-2 font-medium">{r.company}</td>
            <td className="px-3 py-2 text-[var(--cw-muted)] truncate max-w-[180px]">{r.from || "—"}</td>
            <td className="px-3 py-2 truncate max-w-[240px]">{r.subject || "—"}</td>
            <td className="px-3 py-2 text-[var(--cw-muted)]">{r.when || "—"}</td>
            <td className="px-3 py-2 text-[var(--cw-muted)]">{r.note || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* -------------------- Attach to Existing Deal Modal -------------------- */
function AttachToDealModal({ candidates, onClose, onPick, busy }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/deals/search", { params: { q } });
        setResults(data);
      } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const show = q.trim() ? results : (candidates.length > 0 ? candidates : results);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="attach-to-deal-modal">
      <div className="bg-white rounded-xl w-full max-w-xl max-h-[80vh] flex flex-col">
        <div className="px-6 py-4 border-b flex justify-between items-center"
          style={{ borderColor: "var(--cw-border)" }}>
          <h2 className="cw-h2 inline-flex items-center gap-2">
            <Paperclip size={20} /> Attach to existing deal
          </h2>
          <button onClick={onClose} className="text-[var(--cw-muted)] text-lg">×</button>
        </div>
        <div className="p-6 border-b" style={{ borderColor: "var(--cw-border)" }}>
          <div className="relative">
            <Search size={14} className="absolute top-1/2 -translate-y-1/2 left-3"
              style={{ color: "var(--cw-muted)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search deal by company or domain…"
              className="w-full h-9 pl-9 pr-3 rounded-md border text-sm"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid="attach-search-input" autoFocus />
          </div>
          {!q.trim() && candidates.length > 0 && (
            <div className="mt-3 text-[10px] uppercase tracking-widest text-[var(--cw-muted)]">
              Suggested matches
            </div>
          )}
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          {searching && <div className="text-xs text-[var(--cw-muted)]">Searching…</div>}
          {show.length === 0 && !searching && (
            <div className="text-xs text-[var(--cw-muted)] italic">No matching deals.</div>
          )}
          <div className="space-y-2">
            {show.map((d) => (
              <button key={d.deal_id}
                disabled={busy}
                onClick={() => onPick(d.deal_id)}
                className="w-full text-left rounded-md border p-3 hover:border-[var(--cw-primary)] transition"
                style={{ borderColor: "var(--cw-border)" }}
                data-testid={`attach-pick-${d.deal_id}`}>
                <div className="font-medium">{d.company_name}</div>
                <div className="text-xs text-[var(--cw-muted)] mt-0.5">
                  {d.company_domain || d.matched_on || "no domain"}
                  {d.pipeline_name && ` · ${d.pipeline_name}`}
                  {d.stage_name && ` · ${d.stage_name}`}
                  {d.match_layer && (
                    <span className="ml-2 cw-badge"
                      style={{ background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" }}>
                      {d.match_layer.replace(/_/g, " ")}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------- helpers -------------------- */
function CategoryBadge({ category, term }) {
  if (!category) return null;
  const cfg = {
    likely_new_deal: { label: `New ${term("Deal")}`, bg: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" },
    likely_followup: { label: "Follow-up", bg: "rgba(243,191,92,0.18)", color: "#8A6510" },
    meeting_transcript: { label: "Transcript", bg: "rgba(44,154,82,0.14)", color: "#1F6E3A" },
  }[category];
  if (!cfg) return null;
  return (
    <span className="ml-2 cw-badge"
      style={{ background: cfg.bg, color: cfg.color }}
      data-testid={`category-badge-${category}`}>
      {cfg.label}
    </span>
  );
}

function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Minimal HTML sanitiser: strip <script>, <iframe>, on* handlers, javascript: URLs.
// Rendering in a sandboxed iframe (no allow-scripts) provides the primary
// defence — this pass just keeps the rendered result cleaner.
function sanitizeHtml(html) {
  return String(html)
    .replace(/<\s*script[\s\S]*?<\s*\/\s*script\s*>/gi, "")
    .replace(/<\s*iframe[\s\S]*?<\s*\/\s*iframe\s*>/gi, "")
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/href\s*=\s*("javascript:[^"]*"|'javascript:[^']*')/gi, 'href="#"');
}
